/** A type guard function that determines if an item matches a specific type criteria */
export type Filter<T, T2 extends T> = (item: T) => item is T2;

/**
 * A set that only contains items matching a predicate function.
 * Automatically filters items as they are added, maintaining a clean
 * subset without manual filtering operations.
 */
export default class FilterSet<T, T2 extends T> implements Iterable<T2> {
  private items: Set<T2> = new Set();

  constructor(private predicate: Filter<T, T2>) {}

  /** Adds an item to the set only if it passes the filter predicate */
  addIfValid(item: T) {
    if (this.predicate(item)) {
      this.items.add(item);
    }
  }

  /** Removes an item from the set if it passes the filter predicate */
  remove(item: T) {
    if (this.predicate(item)) {
      this.items.delete(item);
    }
  }

  /** Gets the number of items in the filtered set */
  get size() {
    return this.items.size;
  }

  /** Gets the number of items in the filtered set (alias for size) */
  get length() {
    return this.items.size;
  }

  [Symbol.iterator]() {
    return this.items[Symbol.iterator]();
  }
}
